import { Injectable, Injector, EventEmitter } from "@angular/core";
import {
    LoginResponse,
    OidcSecurityService,
    PublicEventsService,
    EventTypes,
} from "angular-auth-oidc-client";
import { User } from "app/core/models/user";
import { ALNIdentityAuthUser, OidcIdToken } from "./aln-identity-user";
import { environment as env } from "../../../environments/environment";
import { Subscription, of } from "rxjs";
import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { Router } from "@angular/router";
import { SharedService } from "@fuse/services/shared.service";
import { RoleInfo } from "../models/role-info";
import { CookieService } from "ngx-cookie-service";
import { IdleTimeoutService } from "app/main/Idle/idleTimeout.service";
import jwt_decode from "jwt-decode";
import { catchError, filter } from "rxjs/operators";
import { ApiService } from "../services/authorizeapi.service";
import { UserInfoModel } from "../models/UserInfoModel";
import { environment } from "environments/environment";
import { CrossTabSessionService } from "../services/cross-tab-session.service";
declare var aptrinsic: any;

@Injectable({
    providedIn: "root",
})
export class ALNIdentityAuthService {
    tokenchange: EventEmitter<string> = new EventEmitter();
    private _user: User = new User();
    private _userInfo: UserInfoModel = new UserInfoModel();
    hidden: boolean = true;
    listofProducts: Array<any> = [];
    rolePermission: RoleInfo;
    hasBeenInitialized: boolean = false; //this should be removed later, we need to move the auth check from contructor here so that we do not need to do this
    private _isAuthorized: boolean = false;
    private isLoggingOut: boolean = false; // Prevent re-entry during cross-tab logout
    private isSilentRenewInProgress: boolean = false; // Set when OIDC fires SilentRenewStarted

    public isAuthorized(delayInSeconds: number): boolean {
        if (!sessionStorage.getItem("exp")) return false;
        const currentTimeInSeconds = Math.floor(new Date().getTime() / 1000);
        const condition =
            Number(sessionStorage.getItem("exp")) >
            currentTimeInSeconds + delayInSeconds;
        return condition;
    }

    get user(): User {
        // if (!this.isAuthorized(-30) && !this._isAuthorized) {
        //     this._user = new User();
        // }
        return this._user;
    }
    private _routeSub1 = new Subscription();
    private _routeSub2 = new Subscription();
    private _crossTabSub = new Subscription();
    private _silentRenewSub = new Subscription();

    constructor(
        public oidcSecurityService: OidcSecurityService,
        private _fuseNavigationService: FuseNavigationService,
        private injector: Injector,
        private router: Router,
        public _data: SharedService,
        private cookieService: CookieService,
        private apiService: ApiService,
        private _cookieService: CookieService,
        private crossTabSessionService: CrossTabSessionService,
        private publicEventsService: PublicEventsService,
    ) {
        // Added below lines to avoid OIDC CheckAuth method for NetDocs iframe issue(Ticket: 27471), as CheckAuth fails on NetDocs Iframe call.
        let currentNetDocIndex = window?.location?.pathname?.indexOf(
            env.netDocsRedirectUrlPath,
        );
        if (currentNetDocIndex && currentNetDocIndex > -1) {
            return;
        }

        this.performAuth();
        this.setupCrossTabListener();
        this.setupSilentRenewListener();
    }

    /**
     * Listen for OIDC SilentRenewStarted event - when fired, the next checkAuth callback is from silent renew
     */
    private setupSilentRenewListener(): void {
        this._silentRenewSub = this.publicEventsService
            .registerForEvents()
            .pipe(
                filter(
                    (notification) =>
                        notification.type === EventTypes.SilentRenewStarted,
                ),
            )
            .subscribe(() => {
                this.isSilentRenewInProgress = true;
                this.performAuth();
            });
    }

    private performAuth() {
        this._routeSub2 = this.oidcSecurityService
            .checkAuth()
            .subscribe((loginResponse: LoginResponse) => {
                // Treat as authenticated when isAuthenticated is true OR when tokens are present
                // (OIDC can return isAuthenticated: false after silent renew despite valid tokens)
                const hasValidTokens = !!(
                    loginResponse?.accessToken && loginResponse?.idToken
                );
                const isAuthenticated = loginResponse?.isAuthenticated;
                this._isAuthorized = isAuthenticated;
                if (isAuthenticated || hasValidTokens) {
                    if (loginResponse?.idToken) {
                        let oidcIdToken: OidcIdToken = jwt_decode(
                            loginResponse.idToken,
                        );
                        if (oidcIdToken?.exp) {
                            sessionStorage.setItem(
                                "exp",
                                oidcIdToken?.exp.toString(),
                            );
                        }
                    }

                    let token = this.oidcSecurityService
                        .getAccessToken()
                        .pipe(
                            catchError((err) => {
                                console.error(
                                    "Error getting Access Token: ",
                                    err,
                                );
                                return of(null);
                            }),
                        )
                        .subscribe((accessToken: string) => {
                            if (accessToken) {
                                sessionStorage.setItem("token", accessToken);

                                this.apiService
                                    .getAuthData()
                                    .subscribe((authToken: string) => {
                                        if (authToken) {
                                            const isSilentRenew =
                                                this.isSilentRenewInProgress;
                                            this.isSilentRenewInProgress = false; // Reset for next time
                                            localStorage.setItem(
                                                "userLoggedIn",
                                                "true",
                                            );
                                            const userData: ALNIdentityAuthUser =
                                                jwt_decode(accessToken);
                                            this.mapUser(
                                                accessToken,
                                                authToken,
                                                isSilentRenew,
                                            );
                                            this.emitTokenChangeEvent(
                                                accessToken,
                                            );
                                            let currentUrl =
                                                window?.location?.href;
                                            if (
                                                !currentUrl ||
                                                currentUrl.indexOf(
                                                    "silent-renew.html",
                                                ) < 0
                                            ) {
                                                // IdleTimeoutService starts for all authenticated users
                                                const idleTimeoutService =
                                                    this.injector.get(
                                                        IdleTimeoutService,
                                                    );
                                                idleTimeoutService.start();
                                            }
                                        } else {
                                            console.error(
                                                "No AUTH token received",
                                            );
                                        }
                                    });
                            } else {
                                console.warn("No Access token received");
                            }
                        });
                } else {
                    //We can ignore this, since this has not been set yet
                    if (!this.hasBeenInitialized) {
                        return;
                    }
                    // Only logout if we're certain the user should be logged out
                    // Don't logout on login page or if user is in the process of logging in
                    const currentPath = window.location.pathname;
                    const isOnLoginPage = currentPath.startsWith("/login");
                    const isOnSilentRenew =
                        currentPath.indexOf("silent-renew") > -1;
                    if (isOnLoginPage || isOnSilentRenew) {
                        console.log(
                            "ALNIdentityAuthService: User not authenticated but on login/silent-renew page, skipping logout",
                        );
                        return;
                    }
                    let val = localStorage.getItem("userLoggedIn");
                    if (val && val === "true") {
                        console.log(
                            "ALNIdentityAuthService: User was logged in but now not authenticated, logging out",
                        );
                        var appURL = `${window.location.origin}/`;
                        if (window.location.toString() === appURL) {
                            this.logout("oidcNoAuth");
                        }
                    } else {
                        console.log(
                            "ALNIdentityAuthService: User not authenticated and userLoggedIn is not true",
                        );
                    }
                }
            });
    }

    /**
     * Setup cross-tab session listener to handle logout events from other tabs
     */
    private setupCrossTabListener(): void {
        this._crossTabSub = this.crossTabSessionService.authEvent$.subscribe(
            (message) => {
                console.log(
                    `ALNIdentityAuthService: Received cross-tab event: ${message.type}`,
                );
                // Handle different types of auth events
                switch (message.type) {
                    case "logout":
                        this.handleCrossTabLogout(
                            message.data.reason || "cross-tab-logout",
                        );
                        break;
                    case "token_expired":
                        this.handleCrossTabTokenExpired();
                        break;
                    case "session_timeout":
                        this.handleCrossTabSessionTimeout();
                        break;
                }
            },
        );
    }

    /**
     * Handle logout event from another tab
     */
    private handleCrossTabLogout(reason: string): void {
        if (this.isLoggingOut) return; // Prevent re-entry
        this.isLoggingOut = true;

        console.log(
            `ALNIdentityAuthService: Logout detected in another tab. Reason: ${reason}`,
        );
        // Clear session data
        sessionStorage.clear();
        localStorage.clear();
        localStorage.setItem("userLoggedIn", "false");
        this.deleteAllExceptRememberMeCookie();

        // Stop idle timeout service
        let idleTimeoutService = this.injector.get(IdleTimeoutService);
        idleTimeoutService.stop();

        // Redirect to login or show logged out message
        // We don't call the full logout flow to avoid infinite loop
        const identity = this.cookieService.get("userIdentity") || "ALN";
        const logoutUrl =
            identity === "ALN"
                ? env.AlnIdentity.logoutUrl
                : env.AderantIdentity.logoutUrl;
        let urlForExternalLogout = `${logoutUrl}&returnUrl=${window.location.origin}&callOrigin=${reason}`;
        // Navigate to external logout
        window.location.href = urlForExternalLogout;
    }

    /**
     * Handle token expiration event from another tab
     */
    private handleCrossTabTokenExpired(): void {
        console.log(
            "ALNIdentityAuthService: Token expiration detected in another tab",
        );
        // Clear tokens
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("exp");
        // Redirect to login to trigger re-authentication
        this.router.navigate(["/login"]);
    }

    /**
     * Handle session timeout event from another tab
     */
    private handleCrossTabSessionTimeout(): void {
        console.log(
            "ALNIdentityAuthService: Session timeout detected in another tab",
        );
        this.handleCrossTabLogout("session-timeout-cross-tab");
    }

    emitTokenChangeEvent(token: string) {
        this.tokenchange.emit(token);
    }

    getTokenChangeEvent() {
        return this.tokenchange;
    }

    login() {
        if (this._cookieService.get("userIdentity")) {
            this.oidcSecurityService.authorize();
        } else {
            const currentUrl =
                window.location.pathname + window.location.search;
            let targetUrl = environment.landingPage;
            // Helper function to check if URL matches a specific path (exact or with trailing content)
            const isPathMatch = (url: string, path: string): boolean => {
                return (
                    url === path ||
                    url.startsWith(path + "/") ||
                    url.startsWith(path + "?")
                );
            };
            // Only append redirectUrl if:
            const shouldAppendRedirect =
                currentUrl &&
                currentUrl !== "/" &&
                !isPathMatch(currentUrl, "/login") &&
                !isPathMatch(currentUrl, "/appgateway/home") &&
                currentUrl !== environment.postLoginUrl;

            if (shouldAppendRedirect) {
                targetUrl += `?redirectUrl=${encodeURIComponent(currentUrl)}`;
            }
            window.location.href = targetUrl;
        }
    }
    ngOnDestroy() {
        this._routeSub1.unsubscribe();
        this._routeSub2.unsubscribe();
        this._crossTabSub.unsubscribe();
        this._silentRenewSub.unsubscribe();
    }
    logout(callOrigin: string) {
        // Broadcast logout to other tabs first
        this.crossTabSessionService.broadcastLogout(callOrigin);
        sessionStorage.clear();
        localStorage.clear();
        localStorage.setItem("userLoggedIn", "false");
        this.deleteAllExceptRememberMeCookie();

        let idleTimeoutService = this.injector.get(IdleTimeoutService);
        idleTimeoutService.stop();
        const identity = this.cookieService.get("userIdentity") || "ALN"; // Default to Company ALN
        const logoutUrl =
            identity === "ALN"
                ? env.AlnIdentity.logoutUrl
                : env.AderantIdentity.logoutUrl;
        let urlForExternalLogout = `${logoutUrl}&returnUrl=${window.location.origin}&callOrigin=${callOrigin}`;
        this.oidcSecurityService
            .logoff()
            .subscribe(
                (result) => (window.location.href = urlForExternalLogout),
            );
    }

    deleteAllExceptRememberMeCookie() {
        let landingPageEmailCookie = "landingPageEmail";
        let userIdentityCookie = "userIdentity";

        // Get all cookies
        const allCookies = this.cookieService.getAll();

        // Loop through all cookies and delete each one except the one we want to keep
        for (const cookieName in allCookies) {
            if (
                allCookies.hasOwnProperty(cookieName) &&
                cookieName !== userIdentityCookie &&
                cookieName !== landingPageEmailCookie
            ) {
                this.cookieService.delete(cookieName);
            }
        }
    }

    profile() {
        window.location.href = `${env.AlnIdentity.myProfileUri}`;
    }
    changePassword() {
        window.location.href = `${env.changePassword}`;
    }

    private mapUser(
        bearerToken: string,
        authToken: string,
        isSilentRenew: boolean = false,
    ) {
        const userData: ALNIdentityAuthUser = jwt_decode(bearerToken);
        const parseObj = (values) =>
            Array.isArray(values)
                ? values.map((v) => JSON.parse(v))
                : [JSON.parse(values)];
        const userInfo = new ALNIdentityAuthUser(userData);
        this._user.userId = userInfo.userId;
        if (!userInfo.userId) {
            this._user.userId = userInfo.sub;
        }
        sessionStorage.setItem("userData", JSON.stringify(userData));

        sessionStorage.setItem("authToken", authToken);
        this._userInfo = jwt_decode(authToken);
        sessionStorage.setItem("userId", JSON.stringify(this._userInfo.userId));

        const isStandaloneEnabled =
            this._userInfo?.standaloneCustomRuleSetsEnabled === "true";
        sessionStorage.setItem(
            "standaloneCustomRuleSetsEnabled",
            JSON.stringify(isStandaloneEnabled),
        );
        this._user.fullName = userInfo.name;
        this._user.userName = userInfo.preferred_username || userInfo.email;
        const company = this.cookieService.get("userIdentity") || "ALN"; // Default to ALN identity
        const stsUrl =
            company === "ALN"
                ? env.AlnIdentity.stsUrl
                : env.AderantIdentity.stsUrl;
        this._user.profileImageURL = userInfo.profilePictureUrl
            ? userInfo.profilePictureUrl.startsWith("https://")
                ? userInfo.profilePictureUrl
                : `${stsUrl}${userInfo.profilePictureUrl}`
            : "assets/images/avatars/profile.jpg"; // Static image if null

        this._user.bearerToken = bearerToken;
        this._user.organisation = this._userInfo.organizationName;
        this._user.applicationRoles = Array.isArray(userInfo.userRoles)
            ? userInfo.userRoles
            : [userInfo.userRoles];
        if (!userInfo.userRoles) {
            this._user.applicationRoles = Array.isArray(userInfo.roles)
                ? userInfo.roles
                : [userInfo.roles];
        }
        sessionStorage.setItem(
            "OrganizationId",
            JSON.stringify(this._userInfo.organizationGUID),
        );
        let userRoleInMilana: string = "";
        this._user.clients = this._userInfo.products
            ? parseObj(this._userInfo.products)
            : [];
        this._user.allClients = this._userInfo.allProducts
            ? parseObj(this._userInfo.allProducts)
            : [];
        if (this._userInfo.products.length > 0) {
            sessionStorage.setItem(
                "userProducts",
                JSON.stringify(this._user.clients),
            );
            sessionStorage.setItem(
                "allProducts",
                JSON.stringify(this._user.allClients),
            );

            try {
                const clientMilana = this._userInfo.products.find(
                    (client) => client.clientName === "Milana",
                );
                userRoleInMilana = clientMilana ? clientMilana.clientRole : "";
            } catch (error) {
                console.error("Error finding client role:", error);
            }
        }

        let productId = "eDockets";
        var products = this._user.clients
            ? this._user.clients.filter((x) => x.clientCode == productId)
            : [];
        this.rolePermission = this.prepareRolePermissionsInfo(products);
        sessionStorage.setItem(
            "roleAccessPermissions",
            JSON.stringify(this.rolePermission),
        );

        if (this._user.clients.length > 0) {
            if (
                this._user.clients.filter((x) => x.clientCode == productId)
                    .length == 0
            ) {
                this._data.sendMessage(true);
            } else {
                this._data.sendMessage(false);
            }
        } else {
            this._data.sendMessage(false);
        }
        //User Management Menu hidden condition for both identity
        // const shouldShowUserManagement = Array.isArray(userData.roles)
        //     ? userData.roles.includes("UserManagementDevAdmin")
        //     : userData.roles === "IDENTITYADMIN";
        const shouldShowUserManagement = Array.isArray(userData.roles)
            ? userData.roles.includes(env.AderantAdminUserRole)
            : false;

        this._fuseNavigationService.updateNavigationItem("usermanagement", {
            hidden: !shouldShowUserManagement,
        });
        if (shouldShowUserManagement) {
            sessionStorage.setItem("UserManagementAdminAccess", "true");
        }

        if (this._user.allClients.length > 0) {
            for (let i = 0; i < this._user.allClients.length; i++) {
                if (this._user.allClients[i]["isSubscribed"]) {
                    this.hidden = false;
                } else {
                    this.hidden = true;
                }

                this._fuseNavigationService.updateNavigationItem(
                    this._user.allClients[i]["clientId"].toLowerCase(),
                    {
                        url: this._user.allClients[i]["clientUri"],
                        hidden: this.hidden,
                    },
                );

                if (this._user.allClients[i].clientName == "Milana") {
                    this._fuseNavigationService.updateNavigationItem(
                        "dashboard",
                        {
                            hidden: !this.rolePermission.DashBoardAccess,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "case-management",
                        {
                            hidden: !this.rolePermission.CaseManagementAccess,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "milanacalendar",
                        {
                            hidden: !this.rolePermission.CalendarAccess,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "Administrators",
                        {
                            hidden: !this.rolePermission.SystemAdministrator,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "autodocket",
                        {
                            hidden: !this.rolePermission.AutoDocketAccess,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "milanaquickdates",
                        {
                            hidden: !(
                                this.rolePermission.QuickDateCalculatorAccess ||
                                this.rolePermission.QuickDatesAccess
                            ),
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "attorneybarsearch",
                        {
                            hidden: !(
                                this.rolePermission.AttorneyAdmissionsAccess ||
                                this.rolePermission.ECFCredentialsAccess
                            ),
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "cr-management",
                        {
                            hidden: !this.rolePermission.CourtRulesReportAccess,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "information-center",
                        {
                            hidden: !this.rolePermission
                                .InformationCenterAccess,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "docketalerts",
                        {
                            hidden: false,
                        },
                    );
                    this._fuseNavigationService.updateNavigationItem(
                        "applications",
                        {
                            hidden: false,
                        },
                    );
                }
            }
        }

        const isAptrinsicSet = sessionStorage.getItem("aptrinsicSet");
        if (!isAptrinsicSet) {
            aptrinsic(
                "identify",
                {
                    //User Fields
                    id: userInfo.email, // Required for logged in app users
                    email: userInfo.email,
                    firstName: userInfo.given_name,
                    lastName: userInfo.family_name,
                    role: userRoleInMilana,
                    signUpDate: new Date(), //unix time in ms
                    plan: "gold", //Custom attributes - please create those custom attributes in Aptrinsic via Account Settings to be tracked.
                    price: 95.5,
                    userHash: "", // optional transient for HMAC identification
                },
                {
                    //Account Fields
                    id: userInfo.organizationGUID, //Required
                    name: userInfo.organizationName,
                    Program: "Platinum", // flat custom attributes
                },
            );
            sessionStorage.setItem("aptrinsicSet", "true");
        }

        // Skip redirect on silent renew - OIDC fires SilentRenewStarted before checkAuth callback
        if (!isSilentRenew) {
            this.redirectUrlBasedonRole();
        }
    }
    redirectUrlBasedonRole() {
        const keyStoragePostLoginUrl: string = "currentPostLoginUrl";
        const landingKey: string = "adLandingPage";

        let url: string;

        let storagePostLoginUrl = sessionStorage.getItem(
            keyStoragePostLoginUrl,
        );
        // If storagePostLoginUrl is set, check for redirectUrl in its query parameters
        if (storagePostLoginUrl) {
            const url = new URL(storagePostLoginUrl, window.location.origin); // Ensure full URL context
            const redirectUrl = url.searchParams.get("redirectUrl");

            if (redirectUrl) {
                // Decode the redirectUrl and perform the redirection
                if (redirectUrl.includes("/autodocket")) {
                    this.navigateBasedOnRole(
                        redirectUrl,
                        this.rolePermission.AutoDocketAccess,
                    );
                } else if (redirectUrl.includes("/case-management")) {
                    this.navigateBasedOnRole(
                        redirectUrl,
                        this.rolePermission.CaseManagementAccess,
                    );
                } else if (redirectUrl.includes("/dashboard")) {
                    this.navigateBasedOnRole(
                        redirectUrl,
                        this.rolePermission.DashBoardAccess,
                    );
                } else if (redirectUrl.includes("/admin-management")) {
                    this.navigateBasedOnRole(
                        redirectUrl,
                        this.rolePermission.SysAdminViewAccess,
                    );
                } else if (
                    redirectUrl.includes("/admissions-and-credentials")
                ) {
                    this.navigateBasedOnRole(
                        redirectUrl,
                        this.rolePermission.AttorneyAdmissionsAccess ||
                            this.rolePermission.ECFCredentialsAccess,
                    );
                } else if (redirectUrl.includes("/milana/ic/home")) {
                    this.navigateBasedOnRole(
                        redirectUrl,
                        this.rolePermission.InformationCenterAccess,
                    );
                } else {
                    this.router.navigateByUrl(redirectUrl);
                }
                return false;
            }
        }

        if (
            storagePostLoginUrl &&
            !storagePostLoginUrl.includes("login_hint")
        ) {
            url = storagePostLoginUrl;
            sessionStorage.removeItem(keyStoragePostLoginUrl);
        }

        const landingPage = JSON.parse(sessionStorage.getItem(landingKey));
        if (landingPage) {
            url = landingPage;
            sessionStorage.removeItem(landingKey);
        }
        if (
            !url ||
            url === "/" ||
            url === "null" ||
            url === "undefined" ||
            url.startsWith("/login")
        ) {
            let originUrl = new URL(window.location.href);
            // Remove the login_hint if present in search params
            if (originUrl.search.includes("login_hint")) {
                originUrl.search = ""; // Clear the search query
            }
            url = originUrl.pathname + originUrl.search + originUrl.hash;
        }
        if (
            !url ||
            url === "/" ||
            url === "null" ||
            url === "undefined" ||
            url.includes("appgateway") ||
            url === "/login"
        ) {
            this.redirectRoute();
        } else {
            if (url.includes("/autodocket")) {
                this.navigateBasedOnRole(
                    url,
                    this.rolePermission.AutoDocketAccess,
                );
            } else if (url.includes("/case-management")) {
                this.navigateBasedOnRole(
                    url,
                    this.rolePermission.CaseManagementAccess,
                );
            } else if (url.includes("/dashboard")) {
                this.navigateBasedOnRole(
                    url,
                    this.rolePermission.DashBoardAccess,
                );
            } else if (url.includes("/admissions-and-credentials")) {
                this.navigateBasedOnRole(
                    url,
                    this.rolePermission.AttorneyAdmissionsAccess ||
                        this.rolePermission.ECFCredentialsAccess,
                );
            } else if (url.includes("/milana/ic/home")) {
                this.navigateBasedOnRole(
                    url,
                    this.rolePermission.InformationCenterAccess,
                );
            } else {
                this.router.navigateByUrl(url);
            }
        }
    }

    navigateBasedOnRole(url: string, rolePermission: boolean) {
        if (rolePermission) {
            this.router.navigateByUrl(url);
        } else {
            this.redirectRoute();
        }
    }

    redirectRoute() {
        if (this.rolePermission.CaseManagementAccess) {
            this.router.navigate(["/case-management"]);
        } else if (this.rolePermission.DashBoardAccess) {
            this.router.navigate(["/dashboard"]);
        } else if (this.rolePermission.AutoDocketAccess) {
            this.router.navigate(["/autodocket"]);
        } else if (this.rolePermission.CalendarAccess) {
            this.router.navigate(["/milana/calendar"]);
        } else if (
            this.rolePermission.QuickDatesAccess ||
            this.rolePermission.QuickDateCalculatorAccess
        ) {
            this.router.navigate(["/milana/quickdates"]);
        } else if (
            this.rolePermission.AttorneyAdmissionsAccess ||
            this.rolePermission.ECFCredentialsAccess
        ) {
            this.router.navigate(["/admissions-and-credentials"]);
        } else if (this.rolePermission.CourtRulesReportAccess) {
            this.router.navigate(["/cr-management"]);
        } else if (this.rolePermission.SystemAdministrator) {
            this.router.navigate(["/admin-management/admin"]);
        } else if (this.rolePermission.InformationCenterAccess) {
            this.router.navigate(["/milana/ic/home"]);
        } else {
            this.router.navigate(["/milana/quickdates"]);
        }
    }

    prepareRolePermissionsInfo(userProducts: any) {
        this.rolePermission = new RoleInfo();
        if (userProducts.length > 0) {
            this.rolePermission.isAdmin =
                userProducts[0].clientRole == "Administrator" ||
                userProducts[0].clientRole == "Admin"
                    ? true
                    : false;
            if (userProducts[0].roleParametersList.length > 0) {
                userProducts[0].roleParametersList.forEach((element) => {
                    var roleParamValue =
                        element.roleParametersValue == "true" ? true : false;
                    element.roleParametersName =
                        element.roleParametersName.trim();
                    if (element.roleParametersName == "Edit")
                        this.rolePermission.EditAccess = roleParamValue;

                    if (element.roleParametersName == "Delete")
                        this.rolePermission.DeleteAccess = roleParamValue;

                    if (element.roleParametersName == "Client View")
                        this.rolePermission.ClientViewAccess = roleParamValue;

                    if (element.roleParametersName == "Client Add")
                        this.rolePermission.ClientAddAccess = roleParamValue;

                    if (element.roleParametersName == "Client Edit")
                        this.rolePermission.ClientEditAccess = roleParamValue;

                    if (element.roleParametersName == "Client Delete")
                        this.rolePermission.ClientDeleteAccess = roleParamValue;

                    if (element.roleParametersName == "Matter View")
                        this.rolePermission.MatterViewAccess = roleParamValue;

                    if (element.roleParametersName == "Matter Add")
                        this.rolePermission.MatterAddAccess = roleParamValue;

                    if (element.roleParametersName == "Matter Edit")
                        this.rolePermission.MatterEditAccess = roleParamValue;

                    if (element.roleParametersName == "Matter Delete")
                        this.rolePermission.MatterDeleteAccess = roleParamValue;

                    if (element.roleParametersName == "Case View")
                        this.rolePermission.CaseViewAccess = roleParamValue;

                    if (element.roleParametersName == "Case Add")
                        this.rolePermission.CaseAddAccess = roleParamValue;

                    if (element.roleParametersName == "Case Edit")
                        this.rolePermission.CaseEditAccess = roleParamValue;

                    if (element.roleParametersName == "Case Delete")
                        this.rolePermission.CaseDeleteAccess = roleParamValue;

                    if (element.roleParametersName == "Docket View")
                        this.rolePermission.DocketViewAccess = roleParamValue;

                    if (element.roleParametersName == "Docket Add")
                        this.rolePermission.DocketAddAccess = roleParamValue;

                    if (element.roleParametersName == "Docket Edit")
                        this.rolePermission.DocketEditAccess = roleParamValue;

                    if (element.roleParametersName == "Docket Delete")
                        this.rolePermission.DocketDeleteAccess = roleParamValue;

                    if (element.roleParametersName == "Event View")
                        this.rolePermission.EventViewAccess = roleParamValue;

                    if (element.roleParametersName == "Event Add")
                        this.rolePermission.EventAddAccess = roleParamValue;

                    if (element.roleParametersName == "Event Edit")
                        this.rolePermission.EventEditAccess = roleParamValue;

                    if (element.roleParametersName == "Event Delete")
                        this.rolePermission.EventDeleteAccess = roleParamValue;

                    if (element.roleParametersName == "System Admin View")
                        this.rolePermission.SysAdminViewAccess = roleParamValue;

                    if (element.roleParametersName == "System Admin Add")
                        this.rolePermission.SysAdminAddAccess = roleParamValue;

                    if (element.roleParametersName == "System Admin Edit")
                        this.rolePermission.SysAdminEditAccess = roleParamValue;

                    if (element.roleParametersName == "System Admin Delete")
                        this.rolePermission.SysAdminDeleteAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Judge/Party View")
                        this.rolePermission.Judge_Party_ViewAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Judge/Party Add")
                        this.rolePermission.Judge_Party_AddAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Judge/Party Edit")
                        this.rolePermission.Judge_Party_EditAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Judge/Party Delete")
                        this.rolePermission.Judge_Party_DeleteAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Cascading Text Tool")
                        this.rolePermission.CascadingTextTool = roleParamValue;

                    if (element.roleParametersName == "Use Reports")
                        this.rolePermission.UseReports = roleParamValue;

                    if (element.roleParametersName == "AutoReport Scheduling")
                        this.rolePermission.AccessAutoReportScheduling =
                            roleParamValue;

                    if (element.roleParametersName == "Use AutoDocket")
                        this.rolePermission.AutoDocketAccess = roleParamValue;

                    if (element.roleParametersName == "Email Case Team")
                        this.rolePermission.EmailCaseTeam = roleParamValue;

                    if (element.roleParametersName == "System Administrator")
                        this.rolePermission.SystemAdministrator =
                            roleParamValue;

                    if (element.roleParametersName == "Quick Dates")
                        this.rolePermission.QuickDatesAccess = roleParamValue;

                    if (element.roleParametersName == "Quick Date Calculator")
                        this.rolePermission.QuickDateCalculatorAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Court Rules Report")
                        this.rolePermission.CourtRulesReportAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Attorney View")
                        this.rolePermission.Attorney_ViewAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Attorney Add")
                        this.rolePermission.Attorney_AddAccess = roleParamValue;

                    if (element.roleParametersName == "Attorney Edit")
                        this.rolePermission.Attorney_EditAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Attorney Delete")
                        this.rolePermission.Attorney_DeleteAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Milana ®")
                        this.rolePermission.CaseManagementAccess =
                            roleParamValue;

                    if (element.roleParametersName == "Dashboard")
                        this.rolePermission.DashBoardAccess = roleParamValue;

                    if (
                        element.roleParametersName == "Calendar SelfAccess Only"
                    )
                        this.rolePermission.CalenderSelfAccess = roleParamValue;

                    if (element.roleParametersName == "Calendar")
                        this.rolePermission.CalendarAccess = roleParamValue;

                    if (element.roleParametersName == "Event Sync Tracking")
                        this.rolePermission.EventSyncTracking = roleParamValue;

                    if (element.roleParametersName == "Attorney Admissions")
                        this.rolePermission.AttorneyAdmissionsAccess =
                            roleParamValue;

                    if (element.roleParametersName == "ECF Credentials")
                        this.rolePermission.ECFCredentialsAccess =
                            roleParamValue;

                    if (
                        element.roleParametersName ==
                        "standaloneCustomRuleSetsEnabled"
                    )
                        this.rolePermission.StandaloneCustomRuleSetsEnabled =
                            roleParamValue;

                    if (element.roleParametersName == "Include ExchangeItemID")
                        this.rolePermission.IncludeExchangeItemId =
                            roleParamValue;

                    if (
                        element.roleParametersName ==
                        "aiAssistantForFirmRulesEnabled"
                    )
                        this.rolePermission.AIAssistantForFirmRulesEnabled =
                            roleParamValue;
                    if (
                        element.roleParametersName ==
                        "Information Center Access"
                    )
                        this.rolePermission.InformationCenterAccess =
                            roleParamValue;
                    if (
                        element.roleParametersName ==
                        "Only Allow Paired Access Attorneys"
                    )
                        this.rolePermission.OnlyAllowPairedAccessAttorneys =
                            roleParamValue;
                });
            }
            if (!!userProducts[0].clientFeatures) {
                userProducts[0].clientFeatures.forEach((clientFeature) => {
                    if (clientFeature.featureName == "DocketDirect") {
                        this.rolePermission.DocketDirectEnabled = true;
                    }
                });
            }
            return this.rolePermission;
        }
        return this.rolePermission;
    }
}
